import {SpinResult} from '@fantasmagames/slot/src/logic/SpinResult';
import {
	AvalancheResult,
	PrototypeSpinResult,
	PrototypeWinLine,
} from '@fantasmagames/slot/src/logic/prototype/PrototypeSpinResult';
import {Communicator} from '@fantasmagames/slot/src/logic/Communicator';
import {ServerEvents} from '@fantasmagames/prototype/src/reels/ServerAvalancheReelsRound';
import {ServerWinLine} from '@fantasmagames/prototype/src/win/ServerWinLine';
import {GameSymbol} from '@fantasmagames/slot/src/modules/reels/data/GameSymbol';
import {ReelConstants, SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {LogicEvents} from '@fantasmagames/slot/src/logic/LogicEvents';
import {IEventManagerWithAsync} from '@fantasmagames/core/src/events/_interfaces/IEventManager';
import {Action} from '@fantasmagames/slot/src/logic/Action';
import {Logic} from '@fantasmagames/slot/src/logic/Logic';
import {PrototypeCommunicator} from '@fantasmagames/slot/src/logic/prototype/PrototypeCommunicator';
import {RelaxStorage} from '@fantasmagames/slot/src/logic/relax/RelaxStorage';
import {RelaxLogicEvents} from '@fantasmagames/slot/src/logic/relax/RelaxLogicEvents';
import {Device} from '@fantasmagames/core/src/device/Device';

export interface FreeSpinsData {
	freeSpinsTotal: number;
	freeSpinsPlayed: number;
}

export interface PrototypeSymbols {
	[serverSym: string]: number;
}

export interface SymbolsReplacements {
	[replaceSymbol: string]: number;
}

export interface Reelset {
	name: string;
	symbols: number[][];
	symbolsIDs: string[][];
}

export interface PlayerMessage {
	title: string;
	message: string;
	nonIntrusive: boolean;
}

export class PrototypeLogic extends Logic {
	protected _avalanches: AvalancheResult[];
	protected _cumulativeWin: number;

	protected _roundTypes: ServerEvents[] = [];
	protected _currentRound: number = 0;
	protected _totalRounds: number = 0;

	protected _beforeFSReels: GameSymbol[] = [];
	protected _initialReels: GameSymbol[] = [];
	protected _newSymbols: GameSymbol[][] = [];
	protected _currentNewSymbolsIndex: number;
	protected _currentRoundSymbols: GameSymbol[];
	protected _stopPositions: number[] = [];

	protected _winLines: ServerWinLine[][] = [];
	protected _currentWinLines: number = 0;

	protected _currentMultiplier: number = 1;

	protected _freeSpins: any[];
	protected _currentFreeSpin: number = 0;

	protected _serverSymbols: PrototypeSymbols = {};
	protected _replacements: SymbolsReplacements = {};

	private _isInFreeSpins: boolean;

	protected _allReelsets: Reelset[];
	protected _currentReelset: Reelset;

	protected _mathVersion: string;

	protected _messagesQueue: PlayerMessage[] = [];

	protected _isInFreeRounds: boolean = false;
	protected _lastFreeSpinsTotal: number = -1;

	protected _serverTime: number;

	protected _replayHttpResponse: any;
	public static CENT_TO_DOLLAR: number = 0.01;

	/* tslint:disable */
	constructor(gameName: string, eventManager: IEventManagerWithAsync, communicator: Communicator, device: Device) {
		super(gameName, eventManager, communicator, device);

		this.intializeServerSymbols();
	}

	/* tslint:enable */

	public createStorage(): void {
		this._storage = new RelaxStorage();
	}

	public createCurrencyFormatter(): void {
		// Already set in System.
		// this._currencyFormatter = new RelaxCurrencyFormatter();
	}

	public resetRound(): void {
		super.resetRound();
		this._currentRound = 0;
	}

	public keepAlivePing(): void {
		this.ping(null);
	}

	public saveData(): void {
		const data = super.saveData();
		this._communicator.saveData(this._sessionID, JSON.stringify(data));
	}

	public getSavedData(): any {}

	public requestSaveData(): void {
		this._communicator.requestSavedData(this._sessionID, (response: any) => {
			const data = response;

			if (data && data.playerData) {
				const playerData = data.playerData;

				if (typeof playerData.quickSpinActive !== 'undefined') {
					this.quickSpinActive = playerData.quickSpinActive;
				}

				if (typeof playerData.spacebarSpinActive !== 'undefined') {
					this.spacebarSpinActive = playerData.spacebarSpinActive;
				}

				if (typeof playerData.dragReelToSpinActive !== 'undefined') {
					this.dragReelToSpinActive = playerData.dragReelToSpinActive;
				}

				if (typeof playerData.quickStopActive !== 'undefined') {
					this.quickStopActive = playerData.quickStopActive;
				}

				if (typeof playerData.fastPlayActive !== 'undefined') {
					this.fastPlayActive = playerData.fastPlayActive;
				}

				if (typeof playerData.soundActive !== 'undefined') {
					this.soundEffectsActive = playerData.soundActive;
				}

				if (typeof playerData.voiceActive !== 'undefined') {
					this.voiceActive = playerData.voiceActive;
				}

				if (typeof playerData.musicActive !== 'undefined') {
					this.musicActive = playerData.musicActive;
				}

				if (typeof playerData.soundAllActive !== 'undefined') {
					this.soundAllActive = playerData.soundAllActive;
				}

				if (typeof playerData.lastBet !== 'undefined') {
					this.lastBet = playerData.lastBet;
					this.betValue = playerData.lastBet;
				}

				if (typeof playerData.lastBetWasMaxBet !== 'undefined') {
					this._lastBetWasMaxBet = playerData.lastBetWasMaxBet;
				}

				if (typeof playerData.skipIntro !== 'undefined') {
					this.skipIntro = playerData.skipIntro;
				}

				if (typeof playerData.autoplayData !== 'undefined') {
					this.autoplayDefault = playerData.autoplayData.autoplayValue;
				}

				if (typeof playerData.lastAutoplayAmount !== 'undefined') {
					this.lastAutoplayAmount = playerData.lastAutoplayAmount;
				}

				this.hasSavedData = true;
			}
		});
	}

	public replay(): void {
		this._eventManager.emit(RelaxLogicEvents.REPLAY_RESPONSE);

		// Implement? This shows the last round.
		throw new Error(`Prototypelogic::Needs to be implemented - replay`);
	}

	public ping(cb: Function): void {
		this._communicator.ping(this._sessionID, (result: string) => {
			const data: any = JSON.parse(result);

			if (this.hasResponseError(data)) {
				this.parseResponseError(data);

				return;
			}

			cb && cb(data);
			this._eventManager.emit(LogicEvents.PING_RESPONSE);
		});
	}

	protected hasResponseError(data: any): boolean {
		if ((data && data.hasOwnProperty('errorCode')) || data.hasOwnProperty('errorcode')) {
			return true;
		}

		return false;
	}

	protected parseResponseError(data: any): void {
		// TODO: Handle erros.
		// errorCode or errorcode : string
		// errorMessage : string
		// this._eventManager.emit();
	}

	public init(cb: Function): void {
		this.parsePlayerSavedData();

		this._communicator.init(this._gameName, (result: string) => {
			const data: any = JSON.parse(result);
			this.reactOnInit(data);
			cb && cb(data);
			this._eventManager.emit(LogicEvents.INIT_RESPONSE);
		});
	}

	public reactOnInit(data: any): void {
		this.parseInit(data);

		if (!__PRODUCTION__) {
			console.warn('init:');
			console.warn(data);
		}
	}

	public spin(cb: Function): void {
		this.preSpinBalance = this.currentBalance;
		this.saveData();
		const betValue: number = this._betValue * 100;

		this._communicator.spin(this._sessionID, betValue, (result: string) => {
			const data: any = JSON.parse(result);
			this.reactOnSpin(data);
			cb && cb(data);
			this._eventManager.emit(LogicEvents.SPIN_RESPONSE);
		});
	}

	public reactOnSpin(data: any): void {
		this.onceBeforeSpinSetup();
		this._spin = this.createSpinResult();
		if (!__PRODUCTION__) {
			console.warn('spin:');
			console.warn(data);
		}
		this.parseSpin(data);
	}

	public freeSpin(cb: Function): void {
		setTimeout(() => {
			this.reactOnFreeSpin(this._freeSpins[this._currentFreeSpin]);
			cb && cb(this._freeSpins[this._currentFreeSpin]);
			this._eventManager.emit(LogicEvents.SPIN_RESPONSE);
		}, 0);
	}

	public reactOnFreeSpin(data: any): void {
		this._currentFreeSpin++;
		this._spin = this.createSpinResult();
		if (!__PRODUCTION__) {
			console.warn('freespin:');
			console.warn(data);
		}
		this.parseSpin(data);
	}

	public respin(cb: Function): void {
		setTimeout(() => {
			cb && cb(null);
			this._eventManager.emit(LogicEvents.RESPIN_RESPONSE);
		});
	}

	public avalanche(cb: Function): void {
		setTimeout(() => {
			cb && cb(null);
			this._eventManager.emit(LogicEvents.AVALANCHE_RESPONSE);
		});
	}

	public getCurrentRoundType(): number {
		return this._roundTypes[this._currentRound];
	}

	public getNextRoundType(): number {
		if (this._currentRound < this._roundTypes.length - 1) {
			return this._roundTypes[this._currentRound + 1];
		}

		return -1;
	}

	public getRoundIndex(): number {
		return this._currentRound;
	}

	public startNextRound(): void {
		if (this._currentRound < this._totalRounds - 1) {
			this._currentRound++;

			this.setResultsForCurrentRound();
		} else {
			this.cleanUp();
		}
	}

	public isInFirstFreeSpin(): boolean {
		return this.isInFreeSpins && this.freespinsTotalAwarded === this.freeSpinsLeft + 1;
	}

	public getPrototypeSpinResults(): PrototypeSpinResult {
		return this._spin as PrototypeSpinResult;
	}

	public getCurrentReelsSymbol(pos: SymbolPosition): GameSymbol {
		return this._currentRoundSymbols.find((item) =>	 {
			return item.position.x === pos.x && item.position.y === pos.y;
		});
	}

	public getFreeSpinsPlayed(): number {
		return this._currentFreeSpin;
	}

	public getFreeSpinsLeft(): number {
		return this._freeSpinsLeft;
	}

	public isEnoughMoneyForSpin(): boolean {
		return this._balance * Logic.CENT_TO_A_DOLLAR >= this.getTotalBet() || this._isFreeSpinsStarted;
	}

	protected intializeServerSymbols(): void {
		throw new Error(`PrototypeLogic::intializeServerSymbols must be implemented in game specific class`);
	}

	protected parseStats(data: any): void {
		this._balance = data.stats.b;
	}

	protected parseMessages(data: any): void {
		for (const mess of data.messages) {
			this._messagesQueue.push({
				message: mess.message,
				title: mess.title,
				nonIntrusive: mess.nonintrusive,
			});
		}
	}

	protected parseLimits(limits: any): void {
		this._defaultBet = limits.defaultBet * Logic.CENT_TO_A_DOLLAR;
		this._betValues = [];
		for (const bet of limits.legalBets) {
			this._betValues.push(bet * Logic.CENT_TO_A_DOLLAR);
		}
	}

	protected parseRefresh(refreshData: any): void {
		// TODO: This is goinng to be part of the restores. But we need a game service (for Desert eg) to determine how we set it up.

		////////////////////////////////////////////////////////////////////
		// Set this to attributes.
		////////////////////////////////////////////////////////////////////

		// const multiplier: number = refreshData.multiplier;
		const ended: boolean = refreshData.ended;
		// const multiplierChoices: number[] = refreshData.multiplierChoices;
		// const stops: number[] = refreshData.stops;
		const roundType: string = refreshData.roundType; // Unsure.
		const winnings: any[] = refreshData.winnings; // Unsure.
		const win: number = refreshData.win; // Setup for restore.
		const symbols: string[][] = refreshData.symbols; // Should be present in all games.
		const multiplierIndex: number = refreshData.multiplierIndex;
		const ba: number = refreshData.ba; // Short form of Bet Amount.

		this.betValue = ba;
	}

	// TODO: default initialization here is temporary - should be removed when real server arrives
	protected parseInit(data: any): void {
		this._sessionID = data.sid;

		if (data.totalFS) {
			this._lastFreeSpinsTotal = data.totalFS;
		}

		if (data.messages) {
			this.parseMessages(data);
		}

		if (data.stats) {
			this.parseStats(data);
		}

		if (data.limits) {
			this.parseLimits(data.limits);
		}

		this._allReelsets = [];
		if (data.config && data.config.reelgrids) {
			const grids = !!data.config.reelgrids ? data.config.reelgrids : data.config.grids;

			for (const grid in grids) {
				if (grids.hasOwnProperty(grid)) {
					const reelgrid = grids[grid];
					let reelIndex: number = -1;
					const currentReelset: Reelset = {name: reelgrid.name, symbols: [], symbolsIDs: []};
					for (const reel of reelgrid.reels) {
						currentReelset.symbols.push([]);
						currentReelset.symbolsIDs.push([]);
						reelIndex++;
						for (const sym of reel) {
							currentReelset.symbols[reelIndex].push(this._serverSymbols[sym]);
							currentReelset.symbolsIDs[reelIndex].push(sym);
						}
					}
					this._allReelsets.push(currentReelset);
				}
			}

			this._currentReelset = this._allReelsets[0];
		} else {
			const defDataString: string =
				'[{"name":"baseGameGrid","reels":[["F9","F9","F9","F9","M4","M4","M4","M4","F8","F8","F8","F8","M3","M3","M3","M3","F9","F9","F9","WR","M1","M1","M1","F6","F6","F6","F6","M2","M2","M2","M2","F9","F9","F9","F9","M4","M4","M4","M4","F7","F7","F7","F7","M1","M1","M1","WR","M5","M5","M5","F7","F7","F7","F7","M4","F8","WR","F9","M5","F6","F6","F6","F6","M3","M3","F7","F7","F6","F6","WR","WR","F9","F9","F6","F6","M4","M4","F9","F9","F9","F9","M1","M1","M1","M1","F6","F6","F6","F6","M3","M3","M3","M3","F9","F9","F9","F9","M3","M3","M3","M3","F7","F7","M5","M5","WR","WR","M4","M4","F8","F8","M5","M5","M5","M5","F6","F6","F6","F6","M5","M5","M5","M5","F9","F9","F9","F9","M5","M5","M5","M5","F7","F7","F7","F7","M3","M3","M3","M3","F6","F6","F6","F6","M5","M5","M5","M5","M4","M4","M4","M4","F9","F9","F9","F9","M3","F8","WR","F7","F9","F6","F6","F6","F6","M2","M2","M2","M2","F8","F8","F8","F8","M5","M5","M5","M5","F6","F6","F6","F6","M4","M4","M4","M4","F8","F8","F8","F8","M1","M1","M1","M1","F9","F9","F9","F9","M4","M4","M4","M4"],["F6","F6","F6","F6","M2","M2","M2","M2","F7","F7","F7","F7","M4","M4","M4","M4","F8","F8","F8","F8","M1","M1","M1","M1","F9","F9","F9","F9","M1","M1","WR","WR","WR","WR","F8","F8","M3","M3","M3","M3","F7","F7","F7","F7","BN","F6","F6","F6","F6","M5","M5","M5","M5","M2","M2","M2","M2","F9","F9","F9","F9","M4","M4","M4","M4","F7","F7","F7","F7","BN","F8","F8","F8","BN","F7","F7","F7","BN","F6","F6","F6","F6","M4","M4","F7","F6","M2","M2","M4","M4","F6","F8","F9","BN","F7","F7","F7","BN","F6","F6","F6","F6","M1","M1","M1","M1","F8","F8","F8","F8","BN","F6","F6","F6","F6","M2","M2","M2","M2","M5","M5","M5","M5","F8","F8","F8","F8","M4","M4","M4","M4","F7","F8","F8","F8","F8","F8","F6","F9","M2","M2","M2","M2","M3","M3","M3","M3","F8","F8","M3","F9","WR","WR","WR","WR","F6","M2","F7","F7","F7","M4","M4","M4","M4","F7","F7","F7","F7","M2","M2","M2","M2","M1","M1","M1","M1","F8","F8","F8","F8","BN","F6","F6","F6","BN","F9","F9","F9","BN","F8","F8","F8","M1","M1","M1","M1","F7","F7","F7","F7"],["M5","M5","M5","M5","F7","F7","F7","F7","M3","M3","M3","M3","F8","F8","F8","F8","M4","M4","M4","M4","F9","F9","F9","F6","RSWR1","F7","F8","RSWR2","F9","F9","F9","BN","F7","F7","F7","F7","M5","M5","M5","M5","F9","F9","F7","F6","WR","WR","WR","WR","F9","F8","F8","F8","F9","F9","F9","F9","M5","M5","M5","M5","F9","F9","F9","F9","M1","M1","M1","M1","F7","F7","F7","F9","F8","WR","WR","WR","WR","F6","F8","F9","M3","M3","M3","M3","F6","F7","BN","M5","M4","F9","F9","M3","M2","RSWR1","F7","RSWR2","F8","F8","F8","M2","M2","M2","M3","F9","F7","F6","F8","M1","M1","M1","F8","F8","F8","F8","M2","M2","M2","M2","F8","F7","RSWR3","F9","RSWR4","F6","F6","F6","F6","M3","M3","M3","M3","F9","F9","F9","F9","M4","M4","M4","M4","F7","F7","F7","F7","RSWR4","F9","F9","F9","M2","M2","M2","M2","F8","F8","F8","F8","M2","M2","M2","M2","F8","F8","F7","F7","F7","RSWR2","F8","F8","F8","M3","M3","M3","F7","F9","BN","F6","F8","M5","M5","M5","M5","F7","F7","F7","F7","BN","F9","F9","F9","BN","F6","F8","F9","BN","F6","M2","F8","F8","F8","F8","M5","M5","M5","M5","F7","F7","F7","F7","M3","M3","M3","M3","F8","F8","F8","F8","M4","M4","M4","M4","F9","F9","F9","RSWR4","F6","F7","F8","RSWR1","F9","F9","F9","BN","F7","F7","F7","F7","M5","M5","M5","M5","F9","F9","F7","F6","WR","WR","WR","WR","F9","F8","F8","F8","F9","F9","F9","F9","M5","M5","M5","M5","F9","F9","F9","F9","M1","M1","M1","M1","F7","F7","F7","F9","F8","WR","WR","WR","WR","F6","F8","F9","M3","M3","M3","M3","F6","F7","BN","M5","M4","F9","F9","M3","M2","RSWR2","RSWR3","F7","F8","F8","M2","M2","M2","M3","F9","F7","RSWR3","F6","F8","M1","M1","M1","F8","F8","F8","F8","M2","M2","M2","M2","F6","RSWR1","F8","F9","F7","RSWR3","F6","F6","F6","M3","M3","M3","M3","F9","F9","F9","F9","M4","M4","M4","M4","F7","F7","F7","F7","F9","F9","F9","F9","M2","M2","M2","M2","F8","F8","F8","F8","M2","M2","M2","M2","F8","F8","F8","RSWR4","F7","F7","F7","F8","F8","F8","M3","M3","M3","F7","F9","BN","F6","F8","M5","M5","M5","M5","F7","F7","F7","F7","BN","F9","F9","F9","BN","F6","F8","F9","BN","F6","M2","F7","F8","F8","F8","F8"],["M3","M3","M3","M3","F8","F9","WR","WR","WR","WR","F7","F6","M4","M4","M4","M4","BN","F8","F8","F8","F8","BN","F9","F9","F9","F9","M1","M1","M1","M1","F8","F8","F8","F8","M5","M5","M5","M5","F6","F6","F6","F6","M2","M2","M2","M2","F8","F6","BN","F7","F7","F7","BN","M4","M4","M4","M4","F9","F9","F9","F9","M2","M2","M2","M2","F6","F6","F6","RSWR2","F8","F8","F8","F8","M5","M5","M5","M5","F9","F9","F9","F9","M1","M1","M1","M1","F7","F7","F7","BN","M3","M3","M3","M3","F8","F8","F8","F8","M2","M2","M2","M2","F6","F6","F6","F6","BN","F7","F7","F7","BN","F9","F9","F9","F7","WR","WR","WR","WR","F9","F8","F8","F8","BN","M3","M3","M3","M3","F7","F7","F7","F7","M5","M5","M5","M5","BN","F9","F9","F9","RSWR1","F9","RSWR3","F7","F7","F6","RSWR4","F9","M4","M4","M4","M4","F6","F8","WR","WR","WR","WR","F7","F9","F8","F8","F8","M5","M5","M5","M5","F9","F9","F9","RSWR3","F8","F8","F8","RSWR4","F7","F7","F7","BN","F6","F6","F6","M4","M4","M4","BN","F9","F9","F9","F9","BN","F8","F6","F6","M3","M3","RSWR3","F7","F8","RSWR4","M2","M3","M3","M3","M3","F8","F9","WR","WR","WR","WR","F7","F6","M4","M4","M4","M4","BN","F8","F8","F8","F8","BN","F9","F9","F9","F9","M1","M1","M1","M1","F8","F8","F8","BN","M5","M5","M5","M5","F6","F6","F6","F6","M2","M2","M2","M2","F8","F6","BN","F7","F7","F7","BN","M4","M4","M4","M4","F9","F9","F9","F9","M2","M2","M2","M2","F6","F6","F6","RSWR2","F8","F8","F8","F8","M5","M5","M5","M5","BN","F9","F9","F9","M1","M1","M1","M1","F7","F7","F7","BN","M3","M3","M3","M3","F8","F8","F8","F8","M2","M2","M2","M2","F6","F6","F6","F6","BN","F7","F7","F7","BN","F9","F9","F9","F7","WR","WR","WR","WR","F9","F8","F8","F8","BN","M3","M3","M3","M3","F7","F7","F7","F7","M5","M5","M5","M5","BN","F9","F9","F9","RSWR1","RSWR2","RSWR4","F6","F7","F8","F9","F9","M4","M4","M4","M4","F6","F8","WR","WR","WR","WR","F7","F9","F8","F8","F8","M5","M5","M5","M5","F9","F9","F9","RSWR3","RSWR2","F8","RSWR1","F6","F7","F7","F7","BN","F6","F6","F6","M4","M4","M4","BN","F9","F9","F9","F9","BN","F8","F6","F6","M3","M3","F7","RSWR1","F8","F6","M2"],["F6","F6","F6","F6","M2","M2","M2","M2","M4","M4","M4","M4","M3","M3","M3","M3","F8","F8","F8","F8","WR","M5","M5","M5","M5","F7","F7","F7","F7","M2","M2","M2","M2","F9","F9","F9","F9","F7","F7","F7","F7","F9","RSWR1","RSWR2","RSWR3","RSWR4","F8","F8","F9","F9","F9","WR","WR","WR","WR","F8","F8","F7","F7","F7","F7","WR","F6","F6","F6","F6","M4","M4","M4","M4","F8","F8","F8","F8","F9","F9","F9","F9","M5","M5","M5","M5","M1","M1","M1","M1","F6","F6","F6","F6","F7","F8","F8","F8","F8","M4","M4","M4","M4","M3","M3","M3","M3","F6","F6","F6","F6","M5","M5","M5","M5","M2","M2","M2","M2","M3","M3","M3","M3","M1","M1","M1","M1","F6","F6","F6","F6","M1","M1","M1","M1","M5","M5","M5","M5","F9","F9","F9","F9","RSWR2","F7","F7","F7","WR","F8","F8","F8","F7","F6","RSWR3","RSWR2","F8","RSWR1","F9","F9","F9","F9","M3","M3","M3","M3","M4","M4","M4","M4","F8","F8","F8","WR","F7","F7","F7","RSWR4","F6","F6","F6","M5","M5","WR","M2","M1","F9","F9","F9","F9","M2","M2","F8","F7","M4","F6","WR","F7","F7","M1","F7","F8","F8","F9","F9","RSWR4","F6","F6","F6","M2","M2","M2","M2","M4","M4","M4","M4","M3","M3","M3","M3","F8","F8","F8","F8","WR","M5","M5","M5","M5","F7","F7","F7","F7","M2","M2","M2","M2","F9","F9","F9","F9","F7","F7","F7","F7","F9","RSWR3","F6","RSWR1","F8","F8","F8","F9","F9","F9","WR","WR","WR","WR","F8","F8","F7","F7","WR","WR","WR","WR","F6","F6","F6","M4","M4","M4","M4","F8","F8","F8","F8","F9","F9","F9","F9","M5","M5","WR","WR","WR","WR","M1","M1","F6","F6","F6","F6","F7","F8","F8","F8","F8","M4","M4","M4","M4","M3","M3","M3","M3","F6","F6","F6","F6","M5","M5","M5","M5","M2","M2","M2","M2","M3","M3","M3","M3","M1","M1","M1","M1","F6","F6","F6","F6","M1","M1","M1","M1","M5","M5","M5","M5","F9","F9","F9","F9","RSWR2","F7","F7","F7","WR","F8","F8","F8","F7","F6","RSWR3","F9","F8","RSWR1","F9","F9","F9","F9","M3","M3","M3","M3","M4","M4","M4","M4","F8","F8","F8","WR","F7","F7","F7","RSWR4","F6","F6","F6","M5","M5","WR","M2","M1","F9","F9","F9","F9","M2","M2","F8","F7","M4","F6","WR","F7","F7","M1","F7","F8","F8","F9","F9"]]},{"name":"freeGameGrid","reels":[["F9","F9","F9","F9","M4","M4","M4","M4","F8","F8","F8","F8","M3","M3","M3","M3","F9","F9","F9","WR","M1","M1","M1","F6","F6","F6","F6","M2","M2","M2","M2","F9","F9","F9","F9","M4","M4","M4","M4","F7","F7","F7","F7","M1","M1","M1","WR","M5","M5","M5","F7","F7","F7","F7","M4","F8","WR","F9","M5","F6","F6","F6","F6","M3","M3","F7","F7","F6","F6","WR","WR","F9","F9","F6","F6","M4","M4","F9","F9","F9","F9","M1","M1","M1","M1","F6","F6","F6","F6","M3","M3","M3","M3","F9","F9","F9","F9","M3","M3","M3","M3","F7","F7","M5","M5","WR","WR","M4","M4","F8","F8","M5","M5","M5","M5","F6","F6","F6","F6","M5","M5","M5","M5","F9","F9","F9","F9","M5","M5","M5","M5","F7","F7","F7","F7","M3","M3","M3","M3","F6","F6","F6","F6","M5","M5","M5","M5","M4","M4","M4","M4","F9","F9","F9","F9","M3","F8","WR","F7","F9","F6","F6","F6","F6","M2","M2","M2","M2","F8","F8","F8","F8","M5","M5","M5","M5","F6","F6","F6","F6","M4","M4","M4","M4","F8","F8","F8","F8","M1","M1","M1","M1","F9","F9","F9","F9","M4","M4","M4","M4"],["F6","F6","F6","F6","M2","M2","M2","M2","F7","F7","F7","F7","M4","M4","M4","M4","F8","F8","F8","F8","M1","M1","M1","M1","F9","F9","F9","F9","M1","M1","M1","M1","F8","F8","F8","F8","M3","M3","M3","M3","F7","F7","F7","F7","F7","F6","F6","F6","F6","M5","M5","M5","M5","M2","M2","M2","M2","F9","F9","F9","F9","M4","M4","M4","M4","F7","F7","F7","F7","F8","F8","F8","F8","F7","F7","F7","F7","F6","F6","F6","F6","F6","M4","M4","F7","F6","M2","M2","M4","M4","F6","F8","F9","F7","F7","F7","F7","F6","F6","F6","F6","F6","M1","M1","M1","M1","F8","F8","F8","F8","F8","F6","F6","F6","F6","M2","M2","M2","M2","M5","M5","M5","M5","F8","F8","F8","F8","M4","M4","M4","M4","F7","F8","WR","WR","WR","WR","F6","F9","M2","M2","M2","M2","M3","M3","M3","M3","F8","F8","M3","F9","WR","WR","WR","WR","F6","M2","F7","F7","F7","M4","M4","M4","M4","F7","F7","F7","F7","M2","M2","M2","M2","M1","M1","M1","M1","F8","F8","F8","F8","F6","F6","F6","F6","F9","F9","F9","F9","F8","F8","F8","F8","M1","M1","M1","M1","F7","F7","F7","F7"],["M5","M5","M5","M5","F7","F7","F7","F7","M3","M3","M3","M3","F8","F8","F8","F8","M4","M4","M4","M4","F9","F9","F9","F6","RSWR1","F7","F8","RSWR2","F9","F9","F9","F9","F7","F7","F7","F7","M5","M5","M5","M5","F9","F9","F7","F6","WR","WR","WR","WR","F9","F8","F8","F8","F9","F9","F9","F9","M5","M5","M5","M5","F9","F9","F9","F9","M1","M1","M1","M1","F7","F7","F7","F9","F8","WR","WR","WR","WR","F6","F8","F9","M3","M3","M3","M3","F6","F7","F7","M5","M4","F9","F9","M3","M2","RSWR1","F7","RSWR2","F8","F8","F8","M2","M2","M2","M3","F9","F7","F6","F8","M1","M1","M1","F8","F8","F8","F8","M2","M2","M2","M2","F8","F7","RSWR3","F9","RSWR4","F6","F6","F6","F6","M3","M3","M3","M3","F9","F9","F9","F9","M4","M4","M4","M4","F7","F7","F7","F7","RSWR4","F9","F9","F9","M2","M2","M2","M2","F8","F8","F8","F8","M2","M2","M2","M2","F8","F8","F7","F7","F7","RSWR2","F8","F8","F8","M3","M3","M3","F7","F9","F6","F6","F8","M5","M5","M5","M5","F7","F7","F7","F7","F9","F9","F9","F9","F6","F6","F8","F9","F6","F6","M2","F8","F8","F8","F8","M5","M5","M5","M5","F7","F7","F7","F7","M3","M3","M3","M3","F8","F8","F8","F8","M4","M4","M4","M4","F9","F9","F9","RSWR4","F6","F7","F8","RSWR1","F9","F9","F9","F9","F7","F7","F7","F7","M5","M5","M5","M5","F9","F9","F7","F6","WR","WR","WR","WR","F9","F8","F8","F8","F9","F9","F9","F9","M5","M5","M5","M5","F9","F9","F9","F9","M1","M1","M1","M1","F7","F7","F7","F9","F8","WR","WR","WR","WR","F6","F8","F9","M3","M3","M3","M3","F6","F7","F7","M5","M4","F9","F9","M3","M2","RSWR2","RSWR3","F7","F8","F8","M2","M2","M2","M3","F9","F7","RSWR3","F6","F8","M1","M1","M1","F8","F8","F8","F8","M2","M2","M2","M2","F6","RSWR1","F8","F9","F7","RSWR3","F6","F6","F6","M3","M3","M3","M3","F9","F9","F9","F9","M4","M4","M4","M4","F7","F7","F7","F7","F9","F9","F9","F9","M2","M2","M2","M2","F8","F8","F8","F8","M2","M2","M2","M2","F8","F8","F8","RSWR4","F7","F7","F7","F8","F8","F8","M3","M3","M3","F7","F9","F6","F6","F8","M5","M5","M5","M5","F7","F7","F7","F7","F9","F9","F9","F9","F6","F6","F8","F9","F9","F6","M2","F7","F8","F8","F8","F8"],["M3","M3","M3","M3","F8","F9","WR","WR","WR","WR","F7","F6","M4","M4","M4","M4","F8","F8","F8","F8","F8","F9","F9","F9","F9","F9","M1","M1","M1","M1","F8","F8","F8","F8","M5","M5","M5","M5","F6","F6","F6","F6","M2","M2","M2","M2","F8","F6","F6","F7","F7","F7","F7","M4","M4","M4","M4","F9","F9","F9","F9","M2","M2","M2","M2","F6","F6","F6","RSWR2","F8","F8","F8","F8","M5","M5","M5","M5","F9","F9","F9","F9","M1","M1","M1","M1","F7","F7","F7","F7","M3","M3","M3","M3","F8","F8","F8","F8","M2","M2","M2","M2","F6","F6","F6","F6","F7","F7","F7","F7","F9","F9","F9","F9","F7","WR","WR","WR","WR","F9","F8","F8","F8","F8","M3","M3","M3","M3","F7","F7","F7","F7","M5","M5","M5","M5","F9","F9","F9","F9","RSWR1","F9","RSWR3","F7","F7","F6","RSWR4","F9","M4","M4","M4","M4","F6","F8","WR","WR","WR","WR","F7","F9","F8","F8","F8","M5","M5","M5","M5","F9","F9","F9","RSWR3","F8","F8","F8","RSWR4","F7","F7","F7","F6","F6","F6","F6","M4","M4","M4","M4","F9","F9","F9","F9","F8","F8","F6","F6","M3","M3","RSWR3","F7","F8","RSWR4","M2","M3","M3","M3","M3","F8","F9","WR","WR","WR","WR","F7","F6","M4","M4","M4","M4","F8","F8","F8","F8","F8","F9","F9","F9","F9","F9","M1","M1","M1","M1","F8","F8","F8","F8","M5","M5","M5","M5","F6","F6","F6","F6","M2","M2","M2","M2","F8","F6","F6","F7","F7","F7","F7","M4","M4","M4","M4","F9","F9","F9","F9","M2","M2","M2","M2","F6","F6","F6","RSWR2","F8","F8","F8","F8","M5","M5","M5","M5","F9","F9","F9","F9","M1","M1","M1","M1","F7","F7","F7","F7","M3","M3","M3","M3","F8","F8","F8","F8","M2","M2","M2","M2","F6","F6","F6","F6","F7","F7","F7","F7","F7","F9","F9","F9","F7","WR","WR","WR","WR","F9","F8","F8","F8","F8","M3","M3","M3","M3","F7","F7","F7","F7","M5","M5","M5","M5","F9","F9","F9","F9","RSWR1","RSWR2","RSWR4","F6","F7","F8","F9","F9","M4","M4","M4","M4","F6","F8","WR","WR","WR","WR","F7","F9","F8","F8","F8","M5","M5","M5","M5","F9","F9","F9","RSWR3","RSWR2","F8","RSWR1","F6","F7","F7","F7","F6","F6","F6","F6","M4","M4","M4","M4","F9","F9","F9","F9","F8","F8","F6","F6","M3","M3","F7","RSWR1","F8","F6","M2"],["F6","F6","F6","F6","M2","M2","M2","M2","M4","M4","M4","M4","M3","M3","M3","M3","F8","F8","F8","F8","WR","M5","M5","M5","M5","F7","F7","F7","F7","M2","M2","M2","M2","F9","F9","F9","F9","F7","F7","F7","F7","F9","RSWR1","RSWR2","RSWR3","RSWR4","F8","F8","F9","F9","F9","F6","F6","F8","F8","F8","F8","F7","F7","F7","F7","WR","F6","F6","F6","F6","M4","M4","M4","M4","F8","F8","F8","F8","F9","F9","F9","F9","M5","M5","M5","M5","M1","M1","M1","M1","F6","F6","F6","F6","F7","F8","F8","F8","F8","M4","M4","M4","M4","M3","M3","M3","M3","F6","F6","F6","F6","M5","M5","M5","M5","M2","M2","M2","M2","M3","M3","M3","M3","M1","M1","M1","M1","F6","F6","F6","F6","M1","M1","M1","M1","M5","M5","M5","M5","F9","F9","F9","F9","RSWR2","F7","F7","F7","WR","F8","F8","F8","F7","F6","RSWR3","RSWR2","F8","RSWR1","F9","F9","F9","F9","M3","M3","M3","M3","M4","M4","M4","M4","F8","F8","F8","WR","F7","F7","F7","RSWR4","F6","F6","F6","M5","M5","WR","M2","M1","F9","F9","F9","F9","M2","M2","F8","F7","M4","F6","WR","F7","F7","M1","F7","F8","F8","F9","F9","RSWR4","F6","F6","F6","M2","M2","M2","M2","M4","M4","M4","M4","M3","M3","M3","M3","F8","F8","F8","F8","WR","M5","M5","M5","M5","F7","F7","F7","F7","M2","M2","M2","M2","F9","F9","F9","F9","F7","F7","F7","F7","F9","RSWR3","F6","RSWR1","F8","F8","F8","F9","F9","F9","F6","F6","F8","F8","F8","F8","F7","F7","F7","F7","WR","F6","F6","F6","F6","M4","M4","M4","M4","F8","F8","F8","F8","F9","F9","F9","F9","M5","M5","M5","M5","M1","M1","M1","M1","F6","F6","F6","F6","F7","F8","F8","F8","F8","M4","M4","M4","M4","M3","M3","M3","M3","F6","F6","F6","F6","M5","M5","M5","M5","M2","M2","M2","M2","M3","M3","M3","M3","M1","M1","M1","M1","F6","F6","F6","F6","M1","M1","M1","M1","M5","M5","M5","M5","F9","F9","F9","F9","RSWR2","F7","F7","F7","WR","F8","F8","F8","F7","F6","RSWR3","F9","F8","RSWR1","F9","F9","F9","F9","M3","M3","M3","M3","M4","M4","M4","M4","F8","F8","F8","WR","F7","F7","F7","RSWR4","F6","F6","F6","M5","M5","WR","M2","M1","F9","F9","F9","F9","M2","M2","F8","F7","M4","F6","WR","F7","F7","M1","F7","F8","F8","F9","F9"]]}]';
			const defData: any = JSON.parse(defDataString);
			for (const reelgrid of defData) {
				let reelIndex: number = -1;
				const currentReelset: Reelset = {name: reelgrid.name, symbols: [], symbolsIDs: []};
				for (const reel of reelgrid.reels) {
					currentReelset.symbols.push([]);
					currentReelset.symbolsIDs.push([]);
					reelIndex++;
					for (const sym of reel) {
						currentReelset.symbols[reelIndex].push(this._serverSymbols[sym]);
						currentReelset.symbolsIDs[reelIndex].push(sym);
					}
				}
				this._allReelsets.push(currentReelset);
			}

			this._currentReelset = this._allReelsets[0];
		}

		if (data.config && data.config.mathversion) {
			this._mathVersion = data.config.mathversion;
		} else {
			this._mathVersion = '';
		}
	}

	protected onceBeforeSpinSetup(): void {
		this._currentFreeSpin = -1;
		this._freeSpins = [];
	}

	protected parseAvalanches(data: any): void {
		this._avalanches = [];
		let avalancheIndex: number = 0;
		for (const avalancheData of data.generations) {
			const avalanche: AvalancheResult = {
				initialSymbols: [],
				symbolsAfterRemoval: [],
				newSymbols: [],
				win: 0,
				winnings: [],
			};

			let reelIndex: number = 0;
			for (const reel of avalancheData.symbols) {
				avalanche.initialSymbols.push([]);
				for (const sym of reel) {
					avalanche.initialSymbols[reelIndex].push(new GameSymbol(this._serverSymbols[sym], {width: 1, height: 1}, {x: -1, y: -1}));
				}
				reelIndex++;
			}

			if (avalancheData.win) {
				avalanche.win = avalancheData.win;
			}

			const removedSymbols: SymbolPosition[] = [];
			if (avalancheData.winnings) {
				for (const win of avalancheData.winnings) {

					const winning: PrototypeWinLine =  {
						winType: win[0],
						winPositions: [],
						winLength: 0,
						win: win[2],
						lineIndex: win[1],
						winSymbol: this._serverSymbols[win[4]],
					};

					for (const wl of win[3]) {
						winning.winPositions.push({x: wl.x, y: wl.y});
						const shouldAddSymbol: boolean = removedSymbols.findIndex( (item) => {
							return item.x === wl.x && item.y === wl.y;
						}) < 0;
						if (shouldAddSymbol) {
							removedSymbols.push({x: wl.x, y: wl.y});
						}
					}
					winning.winLength = winning.winPositions.length;

					avalanche.winnings.push(winning);
				}
			}

			const winSymbolsOnEachPosition: number[] = [0, 0, 0, 0, 0, 0];
			for (const removedSymbol of removedSymbols) {
				winSymbolsOnEachPosition[removedSymbol.x]++;
			}
			reelIndex = 0;
			for (reelIndex = 0; reelIndex < winSymbolsOnEachPosition.length; reelIndex++) {
				const totalWinningSymbols: number = winSymbolsOnEachPosition[reelIndex];
				avalanche.newSymbols.push([]);
				for (let symbolIndex = 0; symbolIndex < totalWinningSymbols; symbolIndex++) {
					const symVal: string = data.generations[avalancheIndex + 1].symbols[reelIndex][symbolIndex];
					avalanche.newSymbols[reelIndex].push(new GameSymbol(this._serverSymbols[symVal], {
						width: 1,
						height: 1,
					}, {x: -1, y: -1}));
				}
			}

			if (avalanche.winnings.length > 0) {
				this._roundTypes.push(ServerEvents.AVALANCHE);
			}

			this._avalanches.push(avalanche);

			avalancheIndex++;
		}
	}

	protected parseSpin(data: any): void {
		this.prepareInGameProgressValues();

		if (data.stats) {
			this._balance = data.stats.b;
			this.getPrototypeSpinResults().setBalance(data.stats.b);
		}

		if (!this.isInFreeSpins && data.totalFS) {
			this._lastFreeSpinsTotal = data.totalFS;
		}

		this._roundTypes.push(ServerEvents.SPIN);

		this.parseAvalanches(data);

		if (data.grid) {
			const currentReelsetName: string = data.grid;
			this.changeCurrentReelset(currentReelsetName);
		}

		if (data.replacements) {
			this._replacements = {};
			for (const replacement in data.replacements) {
				this._replacements[replacement] = data.replacements[replacement];
			}
			this.applyReplacementsToCurrentReelset();
		}

		if (data.initialSymbols) {
			let reelIndex: number = 0;
			for (const reel of data.initialSymbols) {
				let rowIndex: number = 0;
				for (const sym of reel) {
					this._initialReels.push(
						new GameSymbol(
							this._serverSymbols[sym],
							{
								width: 1,
								height: 1,
							},
							{x: reelIndex, y: rowIndex},
						),
					);
					this._currentRoundSymbols.push(
						new GameSymbol(
							this._serverSymbols[sym],
							{
								width: 1,
								height: 1,
							},
							{x: reelIndex, y: rowIndex},
						),
					);
					rowIndex++;
				}
				reelIndex++;
			}
		}

		if (data.stops) {
			for (const stop of data.stops) {
				this._stopPositions.push(stop);
			}

			this.getPrototypeSpinResults().setStopPositions(this._stopPositions);
		}

		if (data.totalwin) {

			this.getPrototypeSpinResults().setRoundWin(data.spinwin);
			this.getPrototypeSpinResults().setTotalWin(data.totalwin);
		} else if (data.cumulativeWins) {
			this.getPrototypeSpinResults().setRoundWin(data.spinwin);
		}
		if (data.mult) {

			this._currentMultiplier = data.mult;
		} else {
			this._currentMultiplier = 1;
		}
		if (data.hasOwnProperty('nscatters')) {

			this.getPrototypeSpinResults().scattersNum = data.nscatters;
		}

		this.parseOtherData(data);

		this.parseFreeSpinsData(data);

		this._totalRounds = this._roundTypes.length;

		this.setResultsForCurrentRound();
	}

	protected parseFreeSpinsData(data: any): void {
		if (data.freespins && data.freespins.length > 0) {
			this.initializeFreeSpinsFromData(data);
		} else {
			if (data.hasOwnProperty('spinsLeft')) {
				this.initializeFreeSpinFromData(data);
			} else {
				this._freeSpinsLeft = -1;
			}
		}

		this.parseTotalWin(data);
	}

	protected parseTotalWin(data: any): void {
		if (data.totalwin) {
			this.getPrototypeSpinResults().setRoundWin(data.spinwin);
			this.getPrototypeSpinResults().setTotalWin(data.totalwin);
		} else if (data.cumulativeWins) {
			this.getPrototypeSpinResults().setRoundWin(data.spinwin);
			this.getPrototypeSpinResults().setTotalWin(data.spinwin);

			this._freeSpinsTotalWin = data.cumulativeWins;
		}
	}

	protected initializeFreeSpinFromData(data: any): void {
		if (data.hasOwnProperty('spinsLeft')) {
			this._freeSpinsLeft = data.spinsLeft;
			if (this._freeSpinsLeft === 0) {
				this._roundTypes.push(ServerEvents.SPIN);
			} else {
				this._roundTypes.push(ServerEvents.FREE_SPIN);
			}

			if (this._isInFreeSpins && data.totalFS !== 0 && this._lastFreeSpinsTotal !== data.totalFS) {
				this._hasFreeSpinsRetrigger = true;
				this._freeSpinsAdded = data.totalFS - this._lastFreeSpinsTotal;
				this._lastFreeSpinsTotal = data.totalFS;
			} else {
				this._hasFreeSpinsRetrigger = false;
			}
		}
	}

	protected initializeFreeSpinsFromData(data: any): void {
		if (data.freespins && data.freespins.length > 0) {
			for (const fs of data.freespins) {
				this._freeSpins.push(fs);
				fs.spinsLeft = data.freespins.length - data.freespins.indexOf(fs) - 1;
			}
			this._roundTypes.push(ServerEvents.FREE_SPIN);
			this._freeSpinsLeft = this._freeSpins.length;
			this._freespinsTotalAwarded = this._freeSpins.length;
			this._currentFreeSpin = -1;

			this._beforeFSReels = [];
			for (const sym of this._initialReels) {
				this._beforeFSReels.push(new GameSymbol(sym.sym, {width: 1, height: 1}, {
					x: sym.position.x,
					y: sym.position.y,
				}));
			}

			this._lastFreeSpinsTotal = data.freespins[0].totalFS;
		}
	}

	protected parseOtherData(data: any): void {

	}

	protected applyReplacementsToCurrentReelset(): void {
		for (const replacement in this._replacements) {
			this._serverSymbols[replacement] = this._serverSymbols[this._replacements[replacement]];
		}

		for (let reelIndex = 0; reelIndex < this._currentReelset.symbols.length; reelIndex++) {
			for (let rowIndex = 0; rowIndex < this._currentReelset.symbols[reelIndex].length; rowIndex++) {
				this._currentReelset.symbols[reelIndex][rowIndex] = this._serverSymbols[
					this._currentReelset.symbolsIDs[reelIndex][rowIndex]
					];
			}
		}
	}

	protected changeCurrentReelset(currentReelsetName: string): void {
		const currentReelsetIndex: number = this._allReelsets.findIndex((item) => {
			return item.name === currentReelsetName;
		});
		if (currentReelsetIndex < 0) {
			throw new Error(`PrototypeLogic::changeCurrentReelset reelset ${currentReelsetName} not found`);
		}
		this._currentReelset = this._allReelsets[currentReelsetIndex];
	}

	protected positionToSymIndex(position: SymbolPosition): number {
		return position.x * ReelConstants.ROW_AMOUNT + position.y;
	}

	protected prepareInGameProgressValues(): void {
		this._roundTypes = [];
		this._initialReels = [];
		this._stopPositions = [];
		this._currentRoundSymbols = [];
		this._newSymbols = [];
		this._currentNewSymbolsIndex = -1;
		this._winLines = [];
		this._currentRound = 0;
		this._totalRounds = 0;
		this._currentWinLines = -1;
	}

	protected setNextAction(): void {
		switch (this.getNextRoundType()) {
			case ServerEvents.SPIN:
				this._nextAction = Action.SPIN;
				break;
			case ServerEvents.AVALANCHE:
				this._nextAction = Action.AVALANCHE;
				break;
			case ServerEvents.RESPIN:
				this._nextAction = Action.RESPIN;
				break;
			case ServerEvents.FREE_SPIN:
				this._nextAction = Action.FREESPIN;
				break;
			default:
				this._nextAction = Action.SPIN;
				break;
		}
	}

	protected cleanUp(): void {
		this.getPrototypeSpinResults().setWins([]);
		this.getPrototypeSpinResults().setRoundWin(0);
	}

	protected setResultsForCurrentRound(): void {
		this.setNextAction();

		(this.getSpinResult() as PrototypeSpinResult).setCurrentRoundSymbols(this._avalanches[this._currentRound].initialSymbols);
		(this.getSpinResult() as PrototypeSpinResult).setRoundWin(this._avalanches[this._currentRound].win);
		(this.getSpinResult() as PrototypeSpinResult).setSymbolsToRemove(this._avalanches[this._currentRound], []);
		if (this._avalanches[this._currentRound].newSymbols.length > 0) {
			(this.getSpinResult() as PrototypeSpinResult).setSymbolsToAdd(this._avalanches[this._currentRound]);
		} else {
			(this.getSpinResult() as PrototypeSpinResult).clearSymbolsToAdd();
		}

		this._freeSpinsTotalWin += this._avalanches[this._currentRound].win;
		this._cumulativeWin += this._avalanches[this._currentRound].win;

		(this.getSpinResult() as PrototypeSpinResult).setWins(this._avalanches[this._currentRound].winnings);
	}

	protected setCurrentRoundLayout(): void {
		this._currentRoundSymbols = [];
		for (const newSym of this._newSymbols[this._currentNewSymbolsIndex]) {
			this._currentRoundSymbols.push(new GameSymbol(newSym.sym, newSym.size, newSym.position));
		}
	}

	protected createSpinResult(): SpinResult {
		return new PrototypeSpinResult();
	}

	protected createCommunicator(communicator: Communicator): void {
		this._communicator = new PrototypeCommunicator();
	}

	public getCurrentSymbolsFromReel(reelIndex: number): number[] {
		const reelSymbols: number[] = [];
		for (const sym of this.getPrototypeSpinResults().reels[reelIndex]) {
			if (sym.position.x === reelIndex) {
				reelSymbols.push(sym.sym);
			}
		}

		return reelSymbols;
	}

	public getCurrentRoundAvalanche(): AvalancheResult {
		return this._avalanches[this._currentRound];
	}

	public get currentBalance(): number {
		return this._balance * Logic.CENT_TO_A_DOLLAR;
	}

	public get stopPositions(): number[] {
		return this._stopPositions;
	}

	public get mathVersion(): string {
		return this._mathVersion;
	}

	public get initialReels(): GameSymbol[] {
		return this._initialReels;
	}

	public get currentMultiplier(): number {
		return this._currentMultiplier;
	}

	public get isInFreeSpins(): boolean {
		return this._isInFreeSpins;
	}

	public get currentReelsetSymbols(): number[][] {
		return this._currentReelset.symbols;
	}

	public get currentReelset(): Reelset {
		return this._currentReelset;
	}

	public get currentRoundSymbols(): GameSymbol[] {
		return this._currentRoundSymbols;
	}

	public get newSymbols(): GameSymbol[][] {
		return this._newSymbols;
	}

	public get messagesQueue(): PlayerMessage[] {
		return this._messagesQueue;
	}

	public set serverTime(value: number) {
		this._serverTime = value;
	}

	public get serverTime(): number {
		return this._serverTime;
	}

	public set replayHttpResponse(value: any) {
		this._replayHttpResponse = value;
	}

	public get replayHttpResponse(): any {
		return this._replayHttpResponse;
	}

	public get freeSpinsTotalWin(): number {
		return this._freeSpinsTotalWin * Logic.CENT_TO_A_DOLLAR;
	}

	public forceSetIsInFreeSpins(value: boolean): void {
		this._isInFreeSpins = value;
	}

	public getTotalBet(): number {
		return this.betValue;
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/logic/prototype/PrototypeLogic.ts